import { useEffect, useState } from 'react';

import { Button } from '@playbooks/ui/components/button';
import { Dialog, DialogContent, DialogTitle } from '@playbooks/ui/components/dialog';
import { Icon } from '@playbooks/ui/components/icon';
import { cn } from '@playbooks/ui/lib/utils';

import { ImageAttachmentCard } from 'components/agent/attachment-card';

const AttachmentGallery = ({ attachments, open, selection, setOpen }) => {
	const [attachment, setAttachment] = useState<any>({});

	// Computed
	const index = attachments.findIndex(v => v.id === attachment.id);

	// Hooks
	useEffect(() => {
		if (open) setAttachment(selection);
	}, [open, selection]);

	// Methods
	const onClose = () => {
		setOpen(false);
	};

	const onSelect = data => {
		setAttachment(data);
	};

	const onPrev = () => {
		if (index === 0) return;
		setAttachment(attachments[index - 1]);
	};

	const onNext = () => {
		if (index === attachments.length - 1) return;
		setAttachment(attachments[index + 1]);
	};

	// Theme
	const thumbnailClassName = id =>
		cn(
			'flex-middle overflow-hidden rounded-control border-2 p-1',
			id === attachment.id ? 'border-focus' : 'border-white/40',
		);

	// Render
	return (
		<Dialog open={open} onOpenChange={nextOpen => !nextOpen && onClose()}>
			<DialogContent className='max-h-[calc(100vh-2rem)] overflow-y-auto sm:max-w-lg overflow-visible w-full'>
				<div className='px-8 py-16 md:px-20 md:py-20 w-full flex-middle h-full overflow-hidden'>
					<Button
						variant='secondary'
						aria-label='Close gallery'
						size='icon'
						onClick={onClose}
						className='bg-surface rounded-full fixed right-4 top-4 z-30'>
						<Icon data-icon='inline-start' type='far' icon='xmark' aria-hidden />
					</Button>
					<div className='fixed top-5 left-4 right-16 flex-start pointer-events-none z-30'>
						<DialogTitle>{attachment.name}</DialogTitle>
					</div>
					<div className='flex-middle h-full w-full z-10'>
						{attachment.preview && (
							<img
								className='max-h-full w-full object-contain shadow-2xl'
								src={attachment.preview}
								alt={attachment.name}
							/>
						)}
					</div>
					<span className='flex-between h-full fixed inset-0 pointer-events-none px-2 z-30'>
						<Button
							variant='secondary'
							size='icon'
							aria-label='Previous attachment'
							onClick={onPrev}
							disabled={index <= 0}
							className='bg-white/10 dark:bg-white/10 rounded-full pointer-events-auto'>
							<Icon data-icon='inline-start' type='far' icon='chevron-left' aria-hidden />
						</Button>
						<Button
							variant='secondary'
							size='icon'
							aria-label='Next attachment'
							onClick={onNext}
							disabled={index === -1 || index === attachments.length - 1}
							className='bg-white/10 dark:bg-white/10 rounded-full pointer-events-auto'>
							<Icon data-icon='inline-start' type='far' icon='chevron-right' aria-hidden />
						</Button>
					</span>
					<div className='fixed bottom-6 left-0 right-0 px-6 z-30'>
						<div className='flex-middle gap-4'>
							{attachments.map(v => (
								<div key={v.id}>
									<Button variant='secondary' onClick={() => onSelect(v)}>
										<div className={thumbnailClassName(v.id)}>
											<ImageAttachmentCard attachment={v} preview={v.preview} />
										</div>
									</Button>
								</div>
							))}
						</div>
					</div>
				</div>
			</DialogContent>
		</Dialog>
	);
};

export { AttachmentGallery };
